import React, { useEffect, useCallback, useState, FC } from 'react';
import HeaderNav from '../components/blocks/headerNav/Header';
import FooterNav from '../components/blocks/footerNav/Footer';
import { getBackgroundTheme, getTextColor } from '../style/';
import { disableBodyScroll, clearAllBodyScrollLocks } from 'body-scroll-lock';
import { ToastContainer } from 'react-toastify';
import NotificationBanner from './blocks/NotificationBanner';
import SiteNotification from './blocks/SiteNotification';
import '../style/normalize.css';
import '../style/toastify-theme.css';
import Loader from '../atoms/LoaderStyled';
import styled from '@emotion/styled';
import OfflineContainer from './blocks/OfflineContainer';
import { differenceInSeconds } from 'date-fns';
import { EXPIRE_SEARCH_HOURS as expireSearchHrs } from '../config/Master';
import { getDate, getLocalItem, renderAvatar } from '../common';
import { NOTIF_TIMEOUT as notifTimeout } from '../config/constants';
import { isDarkMode, removeBodyScroll, ipadNoHomeButton, getCookie } from '../utils/helpers';
import { StickyContainer } from 'react-sticky';
import { useStateCallback } from '../utils/customHooks';
import useMixPanel from '../hooks/useMixPanel';
import usePageViews from '../hooks/usePageViews';
import { useApp } from '../contexts/AppContext';
import useMediaBreakpoints from '../hooks/useMediaBreakpoints';

// Removing speedy false until we can remove css() calls in global module scope.
// @ts-expect-error To be fix in https://reflexmedia.atlassian.net/browse/WYP-16389
import { speedy } from 'glamor';
import VerificationRequired from './pages/Verification/VerificationRequired';
import { EVIDENTLY_FEATURES } from '../constants/evidently';
import { batchEvaluateFeature } from '../sdk/CommonSDK';
import { useLocation, Location } from 'react-router-dom';
import useEvidently from '../hooks/useEvidently';
import evidentlyFeatures from '../utils/evidentlyFeatures';
import { ProfileMaker } from '../models/Profile';
import Footer from './blocks/Footer';
import PWAGlobalBanner from './pwa/PWAGlobalBanner';
import PWAInstallationModal from './pwa/PWAInstallationModal';
import { AccountMaker } from '../models/Account';
import '../style/customSWui.css';
import NotificationBanners from './blocks/NotificationBanners';
import { IContentContainer, IContents, IFeature, IToastCloseBtnProps } from './types/App';
import { DesktopWrapper, MobileWrapper } from './common/appLayout/StyledElements';
import { EVENT_NAMES } from '../constants/mixpanel';
import { mixpanelTrackEvent } from '../utils/mixpanel/common';
import { showPWAInstallationGlobalBanner } from '../utils/pwa';
import breakpoints from '../theme/material/breakpoints';
import GalleryUploadBackgroundProgress from '../modules/gallery/GalleryUpload/components/GalleryUploadBackgroundProgress';
import { SelfieVerificationWall } from './join/selfieVerification/SelfieVerificationWall';
import { ModalProvider } from '../contexts/ModalContext';
import ModalView from './common/modals/ModalView';
import CookieConsentModal from './blocks/CookieConsentModal';
import useCookieConsentCheck from '../hooks/useCookieConsentCheck';
import { updateConsentBasedOnLocation } from '../utils/dataLayer';
// moved edit-profile tracking to profile form submit

speedy(false);

type GetFeatureNamesToEvaluateCallbackType = () => { feature: EVIDENTLY_FEATURES }[];

const publicPages = ['', '/login', '/join', '/restoring'];
const footerlessPages = ['mail', 'search'];

const Contents = styled('div')<IContents>(
  {
    minHeight: '99.97%',
  },
  ({ footer }) =>
    footer && {
      paddingBottom: '135px',
      [breakpoints.down(768.05)]: {
        paddingBottom: '500px',
      },
    },
  ({ isMobile }) =>
    isMobile && {
      paddingBottom: '40px',
    },
  ({ isInMessages }) =>
    isInMessages && {
      paddingBottom: !ipadNoHomeButton ? 13 : 33,
    }
);

const StyledContainer = styled(StickyContainer)(() => {
  return {
    backgroundColor: getBackgroundTheme().surface,
    ...getTextColor().DARK,
  };
});

const ContentContainer = styled('div')<IContentContainer>`
  transition: 0.25s ease-in-out;
  ${props => props.isLoading && 'opacity: 0; height: 0; overflow: hidden;'};
`;

const toasterStyle = (isMobile: boolean) => ({
  top: isMobile ? 0 : 65,
  zIndex: isMobile ? 1001 : 1000,
});

const App: FC = (props: any) => {
  const {
    initiatePendingFav,
    lastSearchTime,
    clearSearch,
    removeLastSearchMeta,
    notification,
    clearNotificationMessage,
    profile,
    account,
    isFetching,
    children,
    loadPageUrl,
    isAuthenticated,
    globalNotification,
    visitMessages,
    generateUrl,
    finishVerification,
    refreshProfile,
    settings,
  } = props;

  const { trackPageView } = useMixPanel();
  const { setAccountHashId, setPwaBannerState, setIsAccountIsMixpanelEligableEnabled } = useApp();
  const { isMobile } = useMediaBreakpoints();
  const location: Location = useLocation();
  const { useFeaturesEvaluation } = useEvidently();

  useFeaturesEvaluation(evidentlyFeatures, ProfileMaker.create(profile));

  usePageViews(({ location, isLeaving }: { location: Location; isLeaving: boolean }) => {
    if (!isLeaving) {
      trackPageView(location.pathname);
    }
  });

  useEffect(() => {
    (async () => {
      const accountModel = AccountMaker.create(account);
      const shouldShow = await showPWAInstallationGlobalBanner(accountModel);

      if (shouldShow.show) {
        mixpanelTrackEvent({
          eventName: EVENT_NAMES.PWA_INSTALL_BANNER_SHOWN,
          payload: {},
        });
      }

      setPwaBannerState({ ...shouldShow });
    })();
  }, [account.hash_id]);

  useEffect(() => {
    window.addEventListener('beforeinstallprompt', e => {
      e.preventDefault();
    });
  }, []);

  const { status, is_paid } = account.id_verification?.data ?? {};

  const [sidebarToggle, setSidebarToggle] = useStateCallback(false);
  const requiredVerification =
    status === 'required' || status === 'pending' || status === 'revoked' || status === 'denied';
  const [verificationStatus, setVerificationStatus] = useState(status);
  const page = location.pathname;
  const isPublic = Boolean(publicPages.indexOf(page) >= 0);
  const isFooterless = isMobile || Boolean(footerlessPages.indexOf(page.split('/')[1]) >= 0);
  const avatar = profile && profile.data && profile.data.avatar && profile.data.avatar.data;
  const nagNotification = null;
  const darkMode = isDarkMode();

  // Cookie consent check for CPPA compliance
  const {
    shouldShowModal: shouldShowCookieConsentModal,
    isGpcActive,
    isQualified,
    isLoading,
  } = useCookieConsentCheck();

  // Initialize cookie consent on app load for authenticated users
  // This updates gtag consent based on user qualification and cookie preference
  useEffect(() => {
    // Guard: Don't run until qualification check is complete (prevents false/premature isQualified)
    // isLoading ensures the hook has finished checking qualification
    // profile?.data ensures we have location data needed for the check
    // isQualified !== undefined ensures we have a definitive qualification status
    if (isLoading || !profile?.data || !settings?.cookie_consent?.enabled || isQualified === undefined) {
      return;
    }

    // Get current cookie consent value
    const cookieConsent = getCookie('cookie_consent');

    // Update consent based on isQualified from useCookieConsentCheck hook
    // This will:
    // - Grant consent for non-CPPA qualified users
    // - Respect cookie preference for CPPA-qualified users (e.g., California)
    updateConsentBasedOnLocation(isQualified, cookieConsent);
  }, [isQualified, isLoading]); // Run when qualification status changes after loading completes
  const [isCookieConsentModalOpen, setIsCookieConsentModalOpen] = useState(shouldShowCookieConsentModal);

  // Update modal state when GPC status or consent check changes
  useEffect(() => {
    // Prevent modal from showing when GPC is active
    if (isGpcActive) {
      setIsCookieConsentModalOpen(false);
      return;
    }
    setIsCookieConsentModalOpen(shouldShowCookieConsentModal);
  }, [shouldShowCookieConsentModal, isGpcActive]);

  const checkLastSearchTime = useCallback(() => {
    const secondDiff = process.env.REACT_APP_ENV !== 'production' ? 60 : expireSearchHrs;

    if (lastSearchTime === null) {
      return;
    }

    const shouldExpireSearch = differenceInSeconds(getDate(), new Date(lastSearchTime)) >= secondDiff;

    if (shouldExpireSearch && navigator.onLine) {
      clearSearch();
      removeLastSearchMeta();
    }
  }, [lastSearchTime]);

  const checkPendingFav = useCallback(() => {
    if (navigator.onLine) {
      const pendingFav = getLocalItem('pending_fav');

      if (pendingFav) {
        const item = JSON.parse(pendingFav);

        item && initiatePendingFav(item.profile_id);
      }
    }
  }, []);

  useEffect(() => {
    window.addEventListener('online', checkPendingFav);
    window.addEventListener('offline', checkPendingFav);
    checkLastSearchTime();

    return () => {
      window.removeEventListener('online', checkPendingFav);
      window.removeEventListener('offline', checkPendingFav);
    };
  }, []);

  useEffect(() => {
    if (status) {
      setVerificationStatus(status);
    }
  }, [status]);

  const toggleSidebar = () => {
    const sidebarParent = document.getElementById('sidebar-parent');

    if (!sidebarParent) return;

    setSidebarToggle(!sidebarToggle, (newState: boolean) => {
      if (newState) {
        disableBodyScroll(sidebarParent);
      } else {
        clearAllBodyScrollLocks();
        removeBodyScroll();
      }
    });
  };

  const hideSidebar = () => {
    setSidebarToggle(false, () => {
      clearAllBodyScrollLocks();
      removeBodyScroll();
    });
  };

  const ToastCloseBtn = ({ closeToast }: IToastCloseBtnProps) => (
    <div onClick={closeToast}>
      <i className="toastify__close">
        <svg viewBox="0 0 12 12" height="12" width="12" version="1.1" xmlns="http://www.w3.org/2000/svg">
          <line x1="1" y1="11" x2="11" y2="1" stroke="#FFF" strokeWidth="3" />
          <line x1="1" y1="1" x2="11" y2="11" stroke="#FFF" strokeWidth="3" />
        </svg>
      </i>
    </div>
  );

  const getFeatureNamesToEvaluate = useCallback<GetFeatureNamesToEvaluateCallbackType>(() => {
    const evaluateNoProfileFavorite = () => {
      return null;
    };

    return [evaluateNoProfileFavorite()]
      .filter(feature => feature !== null)
      .map(feature => feature as { feature: EVIDENTLY_FEATURES });
  }, [account?.mandatory_profile_completed, profile?.data?.account_type]);

  useEffect(() => {
    if (account.hash_id) {
      setAccountHashId(account.hash_id);
      setIsAccountIsMixpanelEligableEnabled(account.is_mixpanel_eligible_and_enabled);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [account.hash_id, profile, settings]);

  useEffect(() => {
    const features = getFeatureNamesToEvaluate();

    if (!profile?.data?.hash_id || features.length === 0) return;

    (async () => {
      try {
        const response = await batchEvaluateFeature({ userId: profile.data.hash_id, features });

        response.data.map((feature: IFeature) => {
          const { reason, variation, userId, featureName } = feature;

          props.storeEvidently({ [featureName]: { variation, userId, reason } });
        });
      } catch (error) {
        console.error('batchEvaluateFeature', { error, features });
      }
    })();
  }, [profile?.data?.hash_id, getFeatureNamesToEvaluate]);

  return (
    <StyledContainer id="main">
      <style
        dangerouslySetInnerHTML={{
          __html: `
          .dash { background: ${isDarkMode() ? '#020d15' : '#F4F7FA'}}`,
        }}
      />

      <SelfieVerificationWall isJoinFlow={false} />

      {profile && globalNotification && globalNotification.id && (
        <SiteNotification
          notification={globalNotification}
          acctType={profile && profile.data && profile.data.account_type}
        />
      )}
      {!sessionStorage.getItem('hasAddedPhoto') && <PWAGlobalBanner />}
      <PWAInstallationModal />
      <ToastContainer
        className={isPublic ? 'container--public' : ''}
        autoClose={notifTimeout}
        position="top-center"
        closeOnClick={false}
        closeButton={<ToastCloseBtn />}
        style={toasterStyle(isMobile)}
      />
      <GalleryUploadBackgroundProgress />
      <NotificationBanner
        notification={notification}
        clearNotificationMessage={clearNotificationMessage}
        loadPageUrl={loadPageUrl}
        isAuthenticated={isAuthenticated}
        location={location}
        style={toasterStyle(isMobile)}
      />
      <NotificationBanners />
      {account && profile && (
        <HeaderNav
          hasGlobalNotif={globalNotification && globalNotification.messages}
          username={account.username}
          hashId={account.hash_id}
          newFavorites={(account.new_favorites || 0) + (account.new_profile_views || 0)}
          newOffers={account.offer_counts.total}
          messages={account.unread_messages}
          avatar={(avatar && renderAvatar(avatar.urls, true)) || null}
          canUpgrade={Boolean(profile && profile.data && profile.data.account_type === 'Generous') || false}
          credits={account.credits}
          pathname={location.pathname}
          nagNotification={nagNotification}
          isMobile={isMobile}
          isDarkMode={darkMode}
          toggleSidebar={toggleSidebar}
          hideSidebar={hideSidebar}
          requiredVerification={requiredVerification}
          {...props}
        />
      )}
      <CookieConsentModal isOpen={isCookieConsentModalOpen} onClose={() => setIsCookieConsentModalOpen(false)} />
      <Contents className="contents" isMobile={isMobile} footer={!isFooterless} isInMessages={visitMessages}>
        <Loader active={isFetching} style={{ position: 'absolute', width: '100%', top: '40%' }} color={'#E21D4A'} />
        <ModalProvider>
          <ContentContainer isLoading={isFetching}>
            {verificationStatus === 'denied' || (!is_paid && requiredVerification) ? (
              <VerificationRequired
                verificationStatus={verificationStatus}
                generateUrl={generateUrl}
                finishVerification={finishVerification}
                refreshProfile={refreshProfile}
                account={account}
              />
            ) : (
              children || <></>
            )}
          </ContentContainer>
          <ModalView />
        </ModalProvider>
      </Contents>
      <DesktopWrapper>
        <Footer isFooterless={isFooterless} profile={profile} />
      </DesktopWrapper>
      <MobileWrapper>
        {account && profile && (
          <FooterNav
            newFavorites={(account.new_favorites || 0) + (account.new_profile_views || 0)}
            newOffers={account.offer_counts.total}
            messages={account.unread_messages}
            pathname={location.pathname}
            isDarkMode={darkMode}
            requiredVerification={requiredVerification}
            profile={profile}
          />
        )}
      </MobileWrapper>
      <OfflineContainer />
    </StyledContainer>
  );
};

export default App;
